import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Library from "@/pages/Library";
import Favorites from "@/pages/Favorites";
import Notes from "@/pages/Notes";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAds from "@/pages/admin/AdminAds";
import AdminUpload from "@/pages/admin/AdminUpload";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminClients from "@/pages/admin/AdminClients";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import Account from "@/pages/Account";
import Terms from "@/pages/Terms";
import { useContentProtect } from "@/lib/contentProtect";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#0FFFE2] spin-slow" />
  </div>
);

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  useContentProtect();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/" element={<Protected><Library /></Protected>} />
      <Route path="/favorites" element={<Protected><Favorites /></Protected>} />
      <Route path="/notes" element={<Protected><Notes /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="ads" element={<AdminAds />} />
        <Route path="upload" element={<AdminUpload />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="analytics" element={<AdminAnalytics />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <AppShell />
            <Toaster position="bottom-right" richColors />
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
